import { useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProcessTemplateListItemType } from '@econtractor/types';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import { deleteProcessTemplate } from 'api/apiTask';
import ESpinner from 'components/others/Spinner';
import TitleCounter from 'components/others/TitleCounter';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import SolarIcon from 'components/icons/SolarIcon';
import ProcessCategoryFilter from './ProcessCategory/ProcessCategoryFilter';
import ProcessCategoryMenu from './ProcessCategory/ProcessCategoryMenu';
import { useProcessDirRegistry } from './model/useProcessDirList';
import ProcessDirTable from './ProcessDirTable';
import OffProcessDir from './OffProcessDir';

const ProcessDir = () => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const {
        templates,
        showSpinner,
        filterCategoryId,
        setFilterCategoryId,
        reload,
        triggerListReset,
        triggerListRefresh,
    } = useProcessDirRegistry();
    const [editorId, setEditorId] = useState<number | null>(null);
    const [editorOpen, setEditorOpen] = useState(false);

    const openNew = () => {
        setEditorId(null);
        setEditorOpen(true);
    };

    const openEdit = (template: ProcessTemplateListItemType) => {
        setEditorId(template.ID);
        setEditorOpen(true);
    };

    const requestDelete = (template: ProcessTemplateListItemType) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await deleteProcessTemplate(template.ID);
            if (!res?.stat) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            showToast(t('processDir.messDeleted'), 'success');
            reload();
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: t('processDir.confirmDelete', { name: template.name || '' }),
        });
    };

    return (
        <>
            <Row className="task-board-page position-relative">
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('processDir.title')}</h4>
                            <TitleCounter />
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <div className="mb-3 page-toolbar-actions">
                                <Button size="sm" variant="success" className="fs-14 text-nowrap" onClick={openNew}>
                                    <SolarIcon name="plus" className="fs-14 p-0" />
                                    <span className="d-none d-md-inline ms-1">{t('processDir.btnNew')}</span>
                                </Button>
                                <ProcessCategoryMenu />
                                <ListToolBarActions onReset={triggerListReset} onRefresh={triggerListRefresh} />
                                <ProcessCategoryFilter
                                    filterCategoryId={filterCategoryId}
                                    setFilterCategoryId={setFilterCategoryId}
                                />
                            </div>
                        </div>
                    </div>
                </Col>
                <Col sm={12}>
                    {templates == null ? (
                        <ESpinner />
                    ) : (
                        <ProcessDirTable
                            templates={templates}
                            showSpinner={showSpinner}
                            onEdit={openEdit}
                            onDelete={requestDelete}
                        />
                    )}
                </Col>
            </Row>

            <OffProcessDir
                show={editorOpen}
                templateId={editorId}
                onHide={() => setEditorOpen(false)}
                onSaved={reload}
            />
        </>
    );
};

export default ProcessDir;
